/** @jsxImportSource @emotion/react */

import { memo, useEffect } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import {
  FormElementType,
  HazardOptionState,
} from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/HazardLayersForm/HazardBox'
import {
  hazardBoxSectionInfo,
  radioGroupContent,
  hazardBoxSLRSlider,
  sectionTitle,
} from '../HazardControls.styles'
import {
  HazardDetail,
  HazardScenario,
} from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { Slider } from '@app/components/Molecules/Slider'
import { convertToMetersOrCentimeters } from '@app/utils/numbers.utils'
import { RadioGroup } from '@app/components/Atoms/RadioGroup'
import { SortableList } from '@app/components/Atoms/Sortable'
import { formElementContainer } from './HazardControlFormElement.styles'

export const HazardControlFormElement = memo(function HazardControlFormElement({
  hazardDetail,
  formElement,
  hazardOptionState,
  handleParameterChange,
  isDragging = false,
  isActive = false,
}: {
  hazardDetail: HazardDetail
  formElement: FormElementType
  hazardOptionState: HazardOptionState
  handleParameterChange: (key: string, value: string | number) => void
  isDragging?: boolean
  isActive?: boolean
}) {
  const theme = useTheme()

  const hazardParameter = hazardDetail.parameters[formElement.key]
  const hazardOption = hazardOptionState.find((option) => option.key === formElement.key)
  const hasSetValue =
    hazardOption?.value !== undefined && hazardOption?.value !== '' && hazardOption?.value !== null

  const filteredScenarios = (
    scenarios: HazardScenario[],
    parameterKey: string,
  ): HazardScenario[] => {
    const parameterIndex = hazardOptionState.findIndex((option) => option.key === parameterKey)
    const hazardOptions = Object.values(hazardOptionState).slice(0, parameterIndex)
    const res = scenarios.filter((scenario: HazardScenario) => {
      // filter by all 'present' keys in hazardOptionState
      const isAvailable = hazardOptions.every((hazardOption) => {
        if (hazardOption.key === parameterKey) return true
        const value = hazardOption?.value
        if (value === '' || value === null || value === undefined || value === 'Not Specified')
          return true
        if (Array.isArray(value))
          return (
            +scenario.parameters[hazardOption.key] >= value[0] &&
            +scenario.parameters[hazardOption.key] <= value[1]
          )
        if (scenario.parameters[hazardOption.key] === undefined) return true
        return scenario.parameters[hazardOption.key].toString() === value.toString()
      })
      return isAvailable
    })
    return res
  }

  const availableValues = hazardDetail.scenarios
    .map((scenario) => scenario.parameters[formElement.key])
    .filter((value, index, self) => self.indexOf(value) === index)

  // if the availableValues are numbers, sort them
  if (availableValues.every((value) => typeof value === 'number')) {
    availableValues.sort((a: string | number, b: string | number) => {
      const numA = typeof a === 'string' ? parseFloat(a) : (a as number)
      const numB = typeof b === 'string' ? parseFloat(b) : (b as number)
      return numA - numB
    })
  }

  const isAvailableObjectDisabled = (
    scenarios: HazardScenario[],
    key: string,
    value: string | number,
  ): boolean => {
    if (value === '' || value === null || value === undefined || value === 'Not specified')
      return false
    const keyValueFound = filteredScenarios(scenarios, key).some((scenario) => {
      return scenario.parameters[key] === value
    })
    return !keyValueFound
  }

  const availableObjects = availableValues.map((value) => {
    // at least one scenario contains all the above option
    const foundScenario = hazardDetail.scenarios.some((scenario) => {
      return scenario.parameters[formElement.key] === value
    })
    // we know there's no options, return early
    if (!foundScenario) return { value, disabled: true }
    const disabled = isAvailableObjectDisabled(hazardDetail.scenarios, formElement.key, value)

    if (disabled) return { value, disabled }
    return { value }
  })

  // if the current value is not in available values, or is disabled, set it to the nearest available value
  useEffect(() => {
    const nonDisabledObjects = availableObjects.filter((obj) => !obj.disabled)
    const selectedObject = nonDisabledObjects.findIndex(
      (obj) => obj.value.toString() === hazardOption?.value.toString(),
    )
    // Check if selected value isn't valid, i.e. not in available values or is disabled
    if (
      hazardOption?.value &&
      !Array.isArray(hazardOption.value) &&
      selectedObject === -1 &&
      nonDisabledObjects.length > 0
    ) {
      const nearestValue = nonDisabledObjects.reduce((prev, curr) => {
        if (typeof hazardOption.value === 'string' && isNaN(Number(hazardOption.value))) {
          return availableValues.indexOf(prev.value) < availableValues.indexOf(curr.value)
            ? prev
            : curr // Use index comparison if the value is not a valid number
        }
        return Math.abs(+curr.value - +hazardOption.value) <
          Math.abs(+prev.value - +hazardOption.value)
          ? curr
          : prev
      }, nonDisabledObjects[0])
      handleParameterChange(formElement.key, nearestValue.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardOption?.value, availableValues])

  if (!hazardParameter) return null
  if (availableObjects.length <= 1 || !availableValues.length) {
    return null
  }

  let header = null
  let body = null
  let setDefaultValue = null

  if (
    formElement.type === 'slider' ||
    (formElement.type === 'sealevelSlider' && availableValues.length)
  ) {
    const availableSliderValues = availableValues.filter((value) => {
      return availableObjects.some((availableObject) => {
        return availableObject.value === value && !availableObject.disabled
      })
    })
    const default_slider_value: number = +availableSliderValues[0]
    let slider_value: number = default_slider_value
    if (typeof hazardOption?.value === 'number' && (hazardOption.value ?? false)) {
      slider_value = hazardOption?.value as number
    }

    setDefaultValue = () => {
      handleParameterChange(formElement.key, slider_value ?? 0.01)
    }

    header = formElement.title

    body = (
      <>
        {availableSliderValues.length == 0 && (
          <Typography>No results found from current filter</Typography>
        )}
        {availableSliderValues.length > 0 && (
          <Box key={`${formElement.key}-sealevelSlider-box`} css={hazardBoxSLRSlider({ theme })}>
            <Slider
              key={`${formElement.key}-slider`}
              value={slider_value}
              handleValueChange={(value) => handleParameterChange(formElement.key, value)}
              marks={availableObjects.map((availableObject) => ({
                label: convertToMetersOrCentimeters(
                  availableObject.value as number,
                  hazardParameter.unit,
                ),
                value: +availableObject.value,
                disabled: availableObject.disabled,
              }))}
              defaultValue={default_slider_value}
              valueLabelDisplay={hasSetValue ? 'on' : 'off'}
              useOnChangeCommitted
              minimalLabels
              track={false}
            />
          </Box>
        )}
      </>
    )
  } else if (formElement.type === 'radioGroup' && availableValues.length) {
    header = formElement.title
    setDefaultValue = () => {
      handleParameterChange(
        formElement.key,
        availableObjects.filter((aObj) => !aObj.disabled)[0].value,
      )
    }
    body = (
      <>
        <RadioGroup
          css={radioGroupContent({ theme })}
          value={hazardOption?.value ?? ''}
          key={`${formElement.key}-radioGroup`}
          onChange={(event) => handleParameterChange(formElement.key, event.target.value)}
          options={availableObjects.map((availableObject) => ({
            label: hazardParameter.keyValues
              ? hazardParameter.keyValues[availableObject.value as number | string]
              : '',
            value: availableObject.value as number | string,
            selected: hazardOption?.value === availableObject.value,
            disabled: availableObject.disabled,
          }))}
        />
      </>
    )
  }

  if (header && body && setDefaultValue) {
    return (
      <SortableList.Item id={formElement.key}>
        <Box css={formElementContainer({ theme, isDragging, isActive })} key={formElement.key}>
          <Box css={hazardBoxSectionInfo({ theme })}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <SortableList.DragHandle />
              <Typography css={sectionTitle} key={`${formElement.key}-label`}>
                {header}
              </Typography>
            </Box>
          </Box>
          {body}
        </Box>
      </SortableList.Item>
    )
  }
  return null
})
